import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import cloneDeep from 'lodash.clonedeep';
import {
    RANGE_MARK_TYPES,
    ANNOTATION_MARK_TYPES,
    MARK_TYPE,
} from '../../../constants/shared/ProbeDetailsConstants';
import { INTERP, KEY } from '../../../constants/shared/CommonConstants';
import {
    getTRCResult,
    getTRCStatus,
    getOLStatus,
    findFinalTRCResult,
    hasUnofficialResults,
    getVocaboStatus,
    getVocaboResult,
    getStudentResultsByMeasureYearGradePeriod,
    updateStudentObject,
} from '../../../utils/shared/resultReducerHelper';
import { getTrcInterpretation } from '../../../constants/shared/TrcInterpretationMapper';
import { MEASURE, VOCABO_MEASURE } from '../../../constants/shared/MeasuresConstants';
import { getCommonInterpretation } from '../../../constants/shared/CommonInterpretationMapper';
import { isSecure } from '../../../utils/shared/commonUtils';

const DEFAULT_OL_MAX_SCORE = 21;
const { BLANK } = INTERP;
const { MEASURE_RESULT_INTERP, INTERPRETATION } = KEY;

const parseNoResponseMark = ({ event_type: eventType, event_details: eventDetails }) => ({
    eventType,
    eventDetails,
});

const parseRangeMark = ({
    event_type: eventType,
    event_details: {
        source,
        start_position: startPosition,
        end_position: endPosition,
        start_is_insert: startIsInsert,
        end_is_insert: endIsInsert,
    },
}) => {
    return {
        eventType,
        eventDetails: {
            source,
            startPosition,
            endPosition,
            ...(eventType === MARK_TYPE.CORRECT && {
                startIsInsert,
                endIsInsert,
            }),
        },
    };
};

const parseAnnotationMark = ({
    event_type: eventType,
    event_details: {
        source,
        position,
        annotation_content: annotationContent,
        annotation_type: annotationType,
    },
}) => ({
    eventType,
    eventDetails: {
        source,
        position,
        annotationContent,
        annotationType,
    },
});
export const parseEvents = events =>
    events.map(event => {
        if (ANNOTATION_MARK_TYPES.includes(event.event_type)) {
            return parseAnnotationMark(event);
        }
        if (RANGE_MARK_TYPES.includes(event.event_type)) {
            return parseRangeMark(event);
        }
        return parseNoResponseMark(event);
    });

const parseItemLevelDetails = ({ sentences }) =>
    sentences.map(({ is_correct: isCorrect, events }) => ({
        isCorrect,
        events: parseEvents(events),
    }));

const parseOlMeasureScores = ({
    measure,
    score,
    interpretation,
    maxScore = DEFAULT_OL_MAX_SCORE,
}) => ({
    [measure]: {
        score,
        interp: getCommonInterpretation(interpretation),
        maxScore,
    },
});

export const parseOLResult = result => ({
    measure: result.measure,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade,
    formGrade: result.grade,
    formUid: result.form_uid,
    studentId: result.student_uid,
    interp: getCommonInterpretation(result.interpretation) || BLANK,
    score: result.score,
    resultId: result.result_id,
    isComplete: result.is_complete,
    measureScores: parseOlMeasureScores(result),
    itemLevelDetails: parseItemLevelDetails(result.item_level_details),
    assessor: result.staff_uid,
    date: result.client_mod_date,
    isSecure: isSecure(result),
});

export const getTrcDetailsData = ({
    resultProbeUid,
    result_id: resultId,
    results,
    latestWRResults,
    item_level_details: itemLevelDetails,
    assessment_date: modifiedDate,
    max_score: maxScore,
}) => ({
    resultProbeUid: resultProbeUid || resultId,
    itemLevelDetails: results || latestWRResults || itemLevelDetails,
    modifiedDate,
    maxScore,
});

export const parseTrcItemLevelDetails = (students, response, studentId) => {
    if (isEmpty(response)) return students;
    const currentStudent = cloneDeep(students[studentId]);
    const resultDetails = getTrcDetailsData(response);
    currentStudent.results = currentStudent.results.map(result =>
        result.resultId === resultDetails.resultProbeUid ? { ...resultDetails, ...result } : result,
    );
    return { ...students, [studentId]: currentStudent };
};

export const parseTRCData = (trcResponse, stateProperties, setTRCData) => {
    let students = {};
    if (isEmpty(trcResponse)) return students;

    trcResponse.forEach(resultGroup => {
        const {
            period,
            student_uid: studentId,
            result_group_id: resultGroupId,
            grade_level: grade,
            school_year: schoolYear,
            results: trcResults,
            instructional_level: score,
            is_complete: isComplete,
            officialness_rank: officialnessRank,
            final_result_uid: finalResultUid,
        } = resultGroup;
        /**
         * TRC result group has only official results when officialness_rank equals 1 (for BM) or -1 (for PM)
         */
        if (Math.abs(officialnessRank) === 1 || !isComplete) {
            const finalResult = findFinalTRCResult(trcResults, finalResultUid);
            const group = {
                measure: MEASURE.TRC,
                studentId,
                resultGroupId,
                grade,
                period,
                schoolYear,
                score,
                isComplete,
                isUnofficial: hasUnofficialResults(trcResponse, studentId, schoolYear, period),
                interp: getTrcInterpretation(get(resultGroup, 'interpretation')),
                isSecure: isSecure(resultGroup),
            };
            const results = [getTRCResult(group, finalResult)];
            const statuses = [getTRCStatus(group, finalResult)];
            students = setTRCData(stateProperties, students, studentId, statuses, results);
        }
    });
    return students;
};

export const parseVocaboData = (vocaboResponse, stateProperties, setVocaboData) => {
    let students = {};
    if (isEmpty(vocaboResponse)) return students;
    vocaboResponse.student_statuses.forEach(status => {
        const vocaboSubResults = getStudentResultsByMeasureYearGradePeriod(
            vocaboResponse.results,
            status.student_uid,
            null,
            status.school_year,
            status.grade_level,
            status.period,
        );
        const results = [getVocaboResult(status, vocaboSubResults)];
        const statuses = [
            getVocaboStatus(
                status,
                vocaboSubResults.find(result => result.measure === VOCABO_MEASURE.TQVM),
            ),
        ];
        students = setVocaboData(stateProperties, students, status.student_uid, statuses, results);
    });
    return students;
};

const getParsedSpellingMeasureResult = measureResult => ({
    responses: measureResult.responses.map(item => ({
        id: item.item_id,
        outcome: item.outcome,
        response: item.response,
        cssScore: item.response_css_score,
        maxCssScore: item.response_max_css_score,
    })),
});

const getSpellingResult = result => ({
    measure: MEASURE.SPELLING,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    formGrade: result.grade_level,
    studentId: result.student_uid,
    resultId: result.result_id,
    formUid: result.form_id,
    isComplete: result.is_complete,
    measureResult: getParsedSpellingMeasureResult(result.measure_result),
    measureScores: {
        [MEASURE.SPELLING]: {
            score: result.measure_result.score,
            interp: getCommonInterpretation(get(result.measure_result, INTERPRETATION)),
            maxScore: result.measure_result.max_css_score,
            wscScore: result.measure_result.wsc_score,
        },
    },
    date: result.mod_date,
    isSecure: isSecure(result),
});

const getSpellingStatus = result => ({
    measure: MEASURE.SPELLING,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    resultId: result.result_id,
    score: get(result, 'measure_result.score'),
    interp: getCommonInterpretation(get(result, MEASURE_RESULT_INTERP)),
    isComplete: result.is_complete,
    date: result.mod_date,
    isSecure: isSecure(result),
});

export const parseSpellingData = (spellingResponse, stateProperties) => {
    let students = {};
    if (isEmpty(spellingResponse)) return students;
    spellingResponse.forEach(result => {
        const results = [getSpellingResult(result)];
        const statuses = [getSpellingStatus(result)];
        students = updateStudentObject(
            students,
            result.student_uid,
            { results, statuses },
            stateProperties,
        );
    });
    return students;
};

export const parseOLData = (olResponse, stateProperties) => {
    let students = {};
    if (isEmpty(olResponse)) return students;
    olResponse.forEach(result => {
        const olResult = parseOLResult(result);
        const { studentId, schoolYear, period } = olResult;
        const hasUnofficialScore = hasUnofficialResults(olResponse, studentId, schoolYear, period);
        const statuses = [getOLStatus(result, hasUnofficialScore)];
        const results = [olResult];
        students = updateStudentObject(
            students,
            result.student_uid,
            { results, statuses },
            stateProperties,
        );
    });
    return students;
};

const parseGrowthRates = growthRates =>
    growthRates.map(rate => ({
        id: rate.growth_range_id,
        minGain: rate.min_gain,
        maxGain: rate.max_gain,
        growthLevel: rate.growth_level,
    }));

export const parseGrowthZones = growthZones =>
    growthZones.map(zone => ({
        studentId: zone.student_uid,
        grade: zone.grade_level,
        schoolYear: Number(zone.school_year),
        period: zone.period,
        measure: zone.measure,
        score: zone.raw_score,
        growthRates: parseGrowthRates(get(zone, 'growth_rates', [])),
        yearGrowthRates: parseGrowthRates(get(zone, 'year_growth_rates', [])),
        achievedGrowthRate: get(zone, 'achieved_growth_rate'),
        achievedGrowthRateLevel: get(zone, 'achieved_growth_rate_level', ''),
        yearAchievedGrowthRate: get(zone, 'year_achieved_growth_rate'),
        yearAchievedGrowthRateLevel: get(zone, 'year_achieved_growth_rate_level', ''),
    }));
